/**
 * NOTCH: Lookbook by Notch
 * Desktop: hover/focus preview cards. Touch: completed-tap bottom sheet.
 */
(function () {
  if (customElements.get('lookbook-notch-component')) return;

  const OPEN_CLASS = 'is-open';
  const HOVER_MQ = '(hover: hover) and (pointer: fine)';
  const REDUCE_MQ = '(prefers-reduced-motion: reduce)';
  const CLOSE_DELAY_MS = 200;
  const TAP_MOVE_PX = 12;
  const TAP_MAX_MS = 700;
  const CLOSE_EVENT = 'lookbook-notch:close-previews';

  let scrollLockCount = 0;
  let savedScrollY = 0;

  function lockPageScroll() {
    scrollLockCount += 1;
    if (scrollLockCount !== 1) return;
    savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.style.setProperty('--lookbook-notch-scroll-lock-top', `-${savedScrollY}px`);
    document.body.classList.add('lookbook-notch-scroll-lock');
  }

  function unlockPageScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount !== 0) return;
    document.body.classList.remove('lookbook-notch-scroll-lock');
    document.documentElement.style.removeProperty('--lookbook-notch-scroll-lock-top');
    window.scrollTo(0, savedScrollY);
  }

  function stripCloneIds(root) {
    root.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    root.querySelectorAll('label[for]').forEach((el) => el.removeAttribute('for'));
  }

  class LookbookNotchComponent extends HTMLElement {
    constructor() {
      super();
      this.activeHotspot = null;
      this.closeTimer = null;
      this.sheet = null;
      this.sheetHotspot = null;
      this.sheetLastFocus = null;
      this.pointerStart = null;
      this.lastPointerType = 'mouse';
      this.pendingSheetHotspot = null;
      this.suppressClick = false;
      this.viewportHidden = false;

      this.boundOnKeyDown = this.onKeyDown.bind(this);
      this.boundOnPointerDown = this.onPointerDown.bind(this);
      this.boundOnPointerUp = this.onPointerUp.bind(this);
      this.boundOnPointerCancel = this.onPointerCancel.bind(this);
      this.boundOnClickCapture = this.onClickCapture.bind(this);
      this.boundOnFocusIn = this.onFocusIn.bind(this);
      this.boundOnFocusOut = this.onFocusOut.bind(this);
      this.boundOnMouseOver = this.onMouseOver.bind(this);
      this.boundOnMouseOut = this.onMouseOut.bind(this);
      this.boundOnCloseEvent = this.onCloseEvent.bind(this);
      this.boundOnVisibilityChange = this.onVisibilityChange.bind(this);
      this.boundOnSheetKeyDown = this.onSheetKeyDown.bind(this);
      this.boundOnSheetPointerDown = this.onSheetPointerDown.bind(this);
    }

    connectedCallback() {
      this.hotspots = Array.from(this.querySelectorAll('.notch-look__hotspot'));
      this.hotspots.forEach((hotspot) => this.prepareHotspot(hotspot));

      this.addEventListener('keydown', this.boundOnKeyDown);
      this.addEventListener('pointerdown', this.boundOnPointerDown);
      this.addEventListener('pointerup', this.boundOnPointerUp);
      this.addEventListener('pointercancel', this.boundOnPointerCancel);
      this.addEventListener('click', this.boundOnClickCapture, true);
      this.addEventListener('focusin', this.boundOnFocusIn);
      this.addEventListener('focusout', this.boundOnFocusOut);
      this.addEventListener('mouseover', this.boundOnMouseOver);
      this.addEventListener('mouseout', this.boundOnMouseOut);
      document.addEventListener(CLOSE_EVENT, this.boundOnCloseEvent);

      this.mqMobile = window.matchMedia('(max-width: 749px)');
      this.mqTablet = window.matchMedia('(min-width: 750px) and (max-width: 989px)');
      this.mqDesktop = window.matchMedia('(min-width: 990px)');
      this.mqMobile.addEventListener('change', this.boundOnVisibilityChange);
      this.mqTablet.addEventListener('change', this.boundOnVisibilityChange);
      this.mqDesktop.addEventListener('change', this.boundOnVisibilityChange);
      this.onVisibilityChange();
    }

    disconnectedCallback() {
      this.clearCloseTimer();
      this.closeSheet({ restoreFocus: false, restoreScroll: true });
      this.closeActive();
      this.removeEventListener('keydown', this.boundOnKeyDown);
      this.removeEventListener('pointerdown', this.boundOnPointerDown);
      this.removeEventListener('pointerup', this.boundOnPointerUp);
      this.removeEventListener('pointercancel', this.boundOnPointerCancel);
      this.removeEventListener('click', this.boundOnClickCapture, true);
      this.removeEventListener('focusin', this.boundOnFocusIn);
      this.removeEventListener('focusout', this.boundOnFocusOut);
      this.removeEventListener('mouseover', this.boundOnMouseOver);
      this.removeEventListener('mouseout', this.boundOnMouseOut);
      document.removeEventListener(CLOSE_EVENT, this.boundOnCloseEvent);
      if (this.mqMobile) this.mqMobile.removeEventListener('change', this.boundOnVisibilityChange);
      if (this.mqTablet) this.mqTablet.removeEventListener('change', this.boundOnVisibilityChange);
      if (this.mqDesktop) this.mqDesktop.removeEventListener('change', this.boundOnVisibilityChange);
      this.teardownSheetDom();
    }

    sectionRoot() {
      return this.closest('.shopify-section');
    }

    isDesignMode() {
      return this.getAttribute('data-design-mode') === 'true';
    }

    isViewportHidden() {
      if (this.isDesignMode()) return false;
      const hideMobile = this.getAttribute('data-hide-mobile') === 'true';
      const hideTablet = this.getAttribute('data-hide-tablet') === 'true';
      const hideDesktop = this.getAttribute('data-hide-desktop') === 'true';
      if (hideMobile && this.mqMobile && this.mqMobile.matches) return true;
      if (hideTablet && this.mqTablet && this.mqTablet.matches) return true;
      if (hideDesktop && this.mqDesktop && this.mqDesktop.matches) return true;
      return false;
    }

    onVisibilityChange() {
      const hidden = this.isViewportHidden();
      this.viewportHidden = hidden;
      const root = this.sectionRoot();
      if (root) {
        if (hidden) root.setAttribute('inert', '');
        else root.removeAttribute('inert');
      }
      if (hidden) {
        this.closeSheet({ restoreFocus: false, restoreScroll: true });
        this.closeActive();
        this.querySelectorAll('.notch-look__hotspot-card__video').forEach((video) => {
          video.pause();
        });
      }
    }

    canHover() {
      return window.matchMedia(HOVER_MQ).matches;
    }

    prefersReducedMotion() {
      return window.matchMedia(REDUCE_MQ).matches;
    }

    shouldUseSheet(pointerType) {
      const type = pointerType || this.lastPointerType;
      if (type === 'touch' || type === 'pen') return true;
      if (type === 'mouse' && this.canHover()) return false;
      return !this.canHover();
    }

    clearCloseTimer() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    scheduleClose(hotspot) {
      this.clearCloseTimer();
      this.closeTimer = setTimeout(() => {
        this.closeTimer = null;
        if (this.activeHotspot !== hotspot) return;
        if (hotspot.matches(':hover')) return;
        if (hotspot.contains(document.activeElement)) return;
        this.closeHotspot(hotspot);
      }, CLOSE_DELAY_MS);
    }

    prepareHotspot(hotspot) {
      const card = hotspot.querySelector('.notch-look__hotspot-card');
      if (!card) return;

      card.setAttribute('aria-hidden', 'true');

      const video = card.querySelector('.notch-look__hotspot-card__video');
      if (!video) return;

      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.preload = 'metadata';
      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {
        /* ignore */
      }

      this.bindVideoOrientation(video, card);

      const muteBtn = card.querySelector('.notch-look__hotspot-card__mute');
      if (muteBtn) {
        muteBtn.classList.remove('button');
        this.syncMuteControl(muteBtn, true);
        muteBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.toggleMute(hotspot);
        });
      }
    }

    bindVideoOrientation(video, card) {
      const apply = () => this.applyVideoOrientation(video, card);
      video.addEventListener('loadedmetadata', apply);
      if (video.readyState >= 1) apply();
    }

    applyVideoOrientation(video, card) {
      if (!video || !card) return;
      const width = video.videoWidth;
      const height = video.videoHeight;
      if (!width || !height) return;

      const landscape = width > height;
      card.classList.toggle('notch-look__hotspot-card--video-landscape', landscape);
      card.classList.toggle('notch-look__hotspot-card--video-portrait', !landscape);
      card.setAttribute('data-video-orientation', landscape ? 'landscape' : 'portrait');
      card.style.setProperty('--notch-video-aspect-ratio', `${width} / ${height}`);
    }

    getHotspotFromEvent(event) {
      const hotspot = event.target.closest('.notch-look__hotspot');
      return hotspot && this.contains(hotspot) ? hotspot : null;
    }

    onPointerDown(event) {
      if (this.viewportHidden) return;
      if (event.target.closest('.notch-look__hotspot-card__mute')) return;
      if (event.target.closest('.lookbook-notch-sheet')) return;

      this.lastPointerType = event.pointerType || this.lastPointerType;
      const hotspot = this.getHotspotFromEvent(event);

      if (this.shouldUseSheet(event.pointerType)) {
        if (!hotspot || !hotspot.querySelector('.notch-look__hotspot-card')) {
          this.pointerStart = null;
          return;
        }
        this.pointerStart = {
          x: event.clientX,
          y: event.clientY,
          time: Date.now(),
          hotspot,
          pointerId: event.pointerId,
        };
        return;
      }

      if (!hotspot) {
        if (!event.target.closest('.notch-look__hotspot-card')) {
          this.closeActive();
        }
        return;
      }

      this.clearCloseTimer();
      this.openHotspot(hotspot, { withSound: true });
    }

    onPointerUp(event) {
      if (!this.pointerStart) return;
      if (this.pointerStart.pointerId !== event.pointerId) return;

      const start = this.pointerStart;
      this.pointerStart = null;

      const dist = Math.hypot(event.clientX - start.x, event.clientY - start.y);
      const elapsed = Date.now() - start.time;
      if (dist > TAP_MOVE_PX || elapsed > TAP_MAX_MS) {
        this.suppressClick = true;
        return;
      }
      if (!this.shouldUseSheet(event.pointerType || this.lastPointerType)) return;

      this.pendingSheetHotspot = start.hotspot;
    }

    onPointerCancel() {
      this.pointerStart = null;
      this.pendingSheetHotspot = null;
    }

    onClickCapture(event) {
      if (this.viewportHidden) return;
      if (event.target.closest('.lookbook-notch-sheet')) return;

      if (this.suppressClick) {
        this.suppressClick = false;
        this.pendingSheetHotspot = null;
        return;
      }

      const hotspot = this.getHotspotFromEvent(event);
      if (this.pendingSheetHotspot && hotspot === this.pendingSheetHotspot) {
        event.preventDefault();
        event.stopPropagation();
        const details = hotspot.closest('details');
        if (details) details.open = false;
        const toOpen = this.pendingSheetHotspot;
        this.pendingSheetHotspot = null;
        this.openSheet(toOpen);
      }
    }

    onMouseOver(event) {
      if (!this.canHover() || this.shouldUseSheet('mouse')) return;
      const hotspot = this.getHotspotFromEvent(event);
      if (!hotspot) return;
      this.clearCloseTimer();
      this.openHotspot(hotspot, { withSound: false });
    }

    onMouseOut(event) {
      if (!this.canHover() || this.shouldUseSheet('mouse')) return;
      const hotspot = this.getHotspotFromEvent(event);
      if (!hotspot) return;
      const next = event.relatedTarget;
      if (next && hotspot.contains(next)) return;
      this.scheduleClose(hotspot);
    }

    onFocusIn(event) {
      if (this.shouldUseSheet(this.lastPointerType)) return;
      const hotspot = this.getHotspotFromEvent(event);
      if (!hotspot) return;
      this.clearCloseTimer();
      this.openHotspot(hotspot, { withSound: false });
    }

    onFocusOut(event) {
      if (this.shouldUseSheet(this.lastPointerType)) return;
      const hotspot = this.getHotspotFromEvent(event);
      if (!hotspot) return;
      const next = event.relatedTarget;
      if (next && hotspot.contains(next)) return;
      requestAnimationFrame(() => {
        if (!hotspot.contains(document.activeElement)) {
          this.scheduleClose(hotspot);
        }
      });
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        this.clearCloseTimer();
        if (this.sheet && this.sheet.classList.contains('is-open')) {
          this.closeSheet({ restoreFocus: true, restoreScroll: true });
          return;
        }
        this.closeActive();
        return;
      }

      if (event.key !== 'Enter' && event.key !== ' ') return;

      const hotspot = this.getHotspotFromEvent(event);
      if (!hotspot) return;
      if (event.target.closest('.notch-look__hotspot-card__mute')) return;

      if (this.shouldUseSheet(this.lastPointerType)) {
        event.preventDefault();
        this.openSheet(hotspot);
        return;
      }

      if (hotspot.tagName === 'A') return;

      event.preventDefault();
      this.clearCloseTimer();
      if (hotspot.classList.contains(OPEN_CLASS)) {
        this.closeHotspot(hotspot);
      } else {
        this.openHotspot(hotspot, { withSound: true });
      }
    }

    onCloseEvent(event) {
      if (event.detail && event.detail.except === this) return;
      this.closeSheet({ restoreFocus: false, restoreScroll: true });
      this.closeActive();
    }

    broadcastCloseOthers() {
      document.dispatchEvent(
        new CustomEvent(CLOSE_EVENT, { detail: { except: this } })
      );
    }

    openHotspot(hotspot, options = {}) {
      if (!hotspot || this.viewportHidden) return;
      this.clearCloseTimer();

      if (this.activeHotspot && this.activeHotspot !== hotspot) {
        this.closeHotspot(this.activeHotspot);
      }

      const alreadyOpen = hotspot.classList.contains(OPEN_CLASS);
      this.activeHotspot = hotspot;
      hotspot.classList.add(OPEN_CLASS);
      if (hotspot.hasAttribute('aria-expanded') || hotspot.getAttribute('role') === 'button') {
        hotspot.setAttribute('aria-expanded', 'true');
      }

      const card = hotspot.querySelector('.notch-look__hotspot-card');
      if (card) card.setAttribute('aria-hidden', 'false');

      this.playHotspotVideo(hotspot, Boolean(options.withSound), alreadyOpen);
    }

    closeHotspot(hotspot) {
      if (!hotspot) return;
      this.clearCloseTimer();
      hotspot.classList.remove(OPEN_CLASS);
      if (hotspot.hasAttribute('aria-expanded')) {
        hotspot.setAttribute('aria-expanded', 'false');
      }

      const card = hotspot.querySelector('.notch-look__hotspot-card');
      if (card) card.setAttribute('aria-hidden', 'true');

      this.resetHotspotVideo(hotspot);

      if (this.activeHotspot === hotspot) {
        this.activeHotspot = null;
      }
    }

    closeActive() {
      this.clearCloseTimer();
      if (this.activeHotspot) {
        this.closeHotspot(this.activeHotspot);
      }
    }

    ensureSheet() {
      if (this.sheet) return this.sheet;
      const sectionId = this.getAttribute('data-section-id') || '';
      const sheet = document.createElement('div');
      sheet.className = 'lookbook-notch-sheet';
      sheet.hidden = true;
      sheet.innerHTML = `
        <div class="lookbook-notch-sheet__backdrop" data-lookbook-sheet-dismiss></div>
        <div
          class="lookbook-notch-sheet__panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="LookbookNotchSheetTitle-${sectionId}"
          tabindex="-1"
        >
          <div class="lookbook-notch-sheet__chrome">
            <h2 class="lookbook-notch-sheet__title" id="LookbookNotchSheetTitle-${sectionId}">Lookbook preview</h2>
            <button type="button" class="lookbook-notch-sheet__close" data-lookbook-sheet-dismiss aria-label="Close preview">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="lookbook-notch-sheet__scroll">
            <div class="lookbook-notch-sheet__media" hidden></div>
            <div class="lookbook-notch-sheet__video-controls" hidden>
              <button type="button" class="lookbook-notch-sheet__ctrl lookbook-notch-sheet__play">Play</button>
              <button type="button" class="lookbook-notch-sheet__ctrl lookbook-notch-sheet__audio">Unmute</button>
            </div>
            <div class="lookbook-notch-sheet__body"></div>
            <a class="lookbook-notch-sheet__cta button" hidden></a>
          </div>
        </div>
      `;
      document.body.appendChild(sheet);
      sheet.addEventListener('keydown', this.boundOnSheetKeyDown);
      sheet.addEventListener('pointerdown', this.boundOnSheetPointerDown);
      sheet.querySelector('.lookbook-notch-sheet__play').addEventListener('click', (event) => {
        event.preventDefault();
        this.toggleSheetPlayback();
      });
      sheet.querySelector('.lookbook-notch-sheet__audio').addEventListener('click', (event) => {
        event.preventDefault();
        this.toggleSheetAudio();
      });
      this.sheet = sheet;
      return sheet;
    }

    teardownSheetDom() {
      if (!this.sheet) return;
      this.sheet.removeEventListener('keydown', this.boundOnSheetKeyDown);
      this.sheet.removeEventListener('pointerdown', this.boundOnSheetPointerDown);
      if (this.sheet.parentNode) this.sheet.parentNode.removeChild(this.sheet);
      this.sheet = null;
    }

    onSheetPointerDown(event) {
      if (event.target.closest('[data-lookbook-sheet-dismiss]')) {
        event.preventDefault();
        this.closeSheet({ restoreFocus: true, restoreScroll: true });
      }
    }

    onSheetKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeSheet({ restoreFocus: true, restoreScroll: true });
        return;
      }
      if (event.key !== 'Tab' || !this.sheet || !this.sheet.classList.contains('is-open')) return;
      const panel = this.sheet.querySelector('.lookbook-notch-sheet__panel');
      const focusable = this.getSheetFocusable();
      if (!focusable.length) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    getSheetFocusable() {
      if (!this.sheet) return [];
      return Array.from(
        this.sheet.querySelectorAll(
          'a[href]:not([hidden]), button:not([disabled]):not([hidden]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.closest('[hidden]') && el.offsetParent !== null);
    }

    openSheet(hotspot) {
      if (!hotspot || this.viewportHidden) return;
      const sourceCard = hotspot.querySelector('.notch-look__hotspot-card');
      if (!sourceCard) return;

      this.broadcastCloseOthers();
      this.closeActive();

      const sheet = this.ensureSheet();
      const wasOpen = sheet.classList.contains('is-open');
      this.pauseSheetVideo();

      this.sheetLastFocus = hotspot.hasAttribute('tabindex') || hotspot.tagName === 'A' ? hotspot : hotspot;
      this.sheetHotspot = hotspot;
      if (hotspot.hasAttribute('aria-expanded') || hotspot.getAttribute('role') === 'button') {
        hotspot.setAttribute('aria-expanded', 'true');
      }

      const titleEl = sheet.querySelector('.lookbook-notch-sheet__title');
      const mediaHost = sheet.querySelector('.lookbook-notch-sheet__media');
      const bodyHost = sheet.querySelector('.lookbook-notch-sheet__body');
      const cta = sheet.querySelector('.lookbook-notch-sheet__cta');
      const videoControls = sheet.querySelector('.lookbook-notch-sheet__video-controls');

      const sourceTitle = sourceCard.querySelector('.notch-look__hotspot-card__title');
      const sourceBody = sourceCard.querySelector('.notch-look__hotspot-card__body');
      const sourceMedia = sourceCard.querySelector('.notch-look__hotspot-card__media');

      titleEl.textContent = sourceTitle ? sourceTitle.textContent.trim() : 'Lookbook preview';

      mediaHost.replaceChildren();
      mediaHost.hidden = true;
      mediaHost.removeAttribute('data-video-orientation');
      mediaHost.style.removeProperty('--notch-video-aspect-ratio');
      mediaHost.style.removeProperty('--lookbook-sheet-media-ratio');
      videoControls.hidden = true;
      if (sourceMedia) {
        const mediaClone = sourceMedia.cloneNode(true);
        stripCloneIds(mediaClone);
        mediaClone.classList.remove('media');
        mediaClone.classList.add('lookbook-notch-sheet__frame');
        mediaClone.querySelectorAll('.notch-look__hotspot-card__mute').forEach((btn) => btn.remove());
        this.prepareSheetMediaSources(mediaClone);
        mediaHost.appendChild(mediaClone);
        mediaHost.hidden = false;
        const clonedVideo = mediaHost.querySelector('.notch-look__hotspot-card__video');
        const clonedImage = mediaHost.querySelector('.notch-look__hotspot-card__image');
        if (clonedVideo) {
          clonedVideo.muted = true;
          clonedVideo.playsInline = true;
          clonedVideo.setAttribute('playsinline', '');
          clonedVideo.controls = false;
          clonedVideo.preload = 'auto';
          this.bindVideoOrientation(clonedVideo, mediaHost);
          this.applySheetMediaRatio(clonedVideo, mediaHost);
          try {
            clonedVideo.load();
          } catch (e) {
            /* ignore */
          }
          videoControls.hidden = false;
          this.syncSheetVideoButtons(clonedVideo);
        } else if (clonedImage) {
          this.applySheetMediaRatio(clonedImage, mediaHost);
        }
      }

      bodyHost.replaceChildren();
      if (sourceBody) {
        const bodyClone = sourceBody.cloneNode(true);
        stripCloneIds(bodyClone);
        const clonedTitle = bodyClone.querySelector('.notch-look__hotspot-card__title');
        if (clonedTitle) clonedTitle.remove();
        bodyHost.appendChild(bodyClone);
      }

      const productUrl = hotspot.getAttribute('data-product-url');
      if (productUrl) {
        cta.hidden = false;
        cta.setAttribute('href', productUrl);
        cta.textContent = 'View product';
      } else {
        cta.hidden = true;
        cta.removeAttribute('href');
        cta.textContent = '';
      }

      sheet.hidden = false;
      sheet.classList.add('is-open');
      document.body.classList.add('lookbook-notch-sheet-open');
      if (!wasOpen) lockPageScroll();

      const panel = sheet.querySelector('.lookbook-notch-sheet__panel');
      panel.focus();

      const clonedVideo = mediaHost.querySelector('.notch-look__hotspot-card__video');
      if (clonedVideo && !this.prefersReducedMotion()) {
        this.playSheetVideoMuted(clonedVideo);
      }
    }

    closeSheet(options = {}) {
      const restoreFocus = options.restoreFocus !== false;
      const restoreScroll = options.restoreScroll !== false;
      if (!this.sheet || !this.sheet.classList.contains('is-open')) {
        if (this.sheetHotspot && this.sheetHotspot.hasAttribute('aria-expanded')) {
          this.sheetHotspot.setAttribute('aria-expanded', 'false');
        }
        return;
      }

      this.pauseSheetVideo();
      this.sheet.classList.remove('is-open');
      this.sheet.hidden = true;
      document.body.classList.remove('lookbook-notch-sheet-open');
      if (restoreScroll) unlockPageScroll();

      if (this.sheetHotspot && this.sheetHotspot.hasAttribute('aria-expanded')) {
        this.sheetHotspot.setAttribute('aria-expanded', 'false');
      }

      const trigger = this.sheetHotspot;
      this.sheetHotspot = null;
      if (restoreFocus && trigger && typeof trigger.focus === 'function') {
        trigger.focus();
      }
    }

    sheetVideo() {
      return this.sheet ? this.sheet.querySelector('.lookbook-notch-sheet__media .notch-look__hotspot-card__video') : null;
    }

    prepareSheetMediaSources(root) {
      root.querySelectorAll('img').forEach((img) => {
        img.loading = 'eager';
        img.setAttribute('loading', 'eager');
        img.removeAttribute('is');
        const src = img.currentSrc || img.getAttribute('src');
        if (src) img.setAttribute('src', src);
      });
      root.querySelectorAll('video').forEach((video) => {
        video.querySelectorAll('source').forEach((source) => {
          if (source.getAttribute('src') && !source.src) {
            source.src = source.getAttribute('src');
          }
        });
      });
    }

    applySheetMediaRatio(mediaEl, host) {
      if (!mediaEl || !host) return;
      const apply = (width, height) => {
        if (!width || !height) return;
        const ratio = `${width} / ${height}`;
        host.style.setProperty('--lookbook-sheet-media-ratio', ratio);
        mediaEl.style.aspectRatio = ratio;
      };
      if (mediaEl.tagName === 'VIDEO') {
        const run = () => apply(mediaEl.videoWidth, mediaEl.videoHeight);
        mediaEl.addEventListener('loadedmetadata', run, { once: true });
        if (mediaEl.readyState >= 1) run();
        return;
      }
      const run = () => apply(mediaEl.naturalWidth, mediaEl.naturalHeight);
      if (mediaEl.complete && mediaEl.naturalWidth) run();
      else mediaEl.addEventListener('load', run, { once: true });
    }

    playSheetVideoMuted(video) {
      if (!video) return;
      video.muted = true;
      this.syncSheetVideoButtons(video);
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          this.syncSheetVideoButtons(video);
        });
      }
      video.addEventListener('play', () => this.syncSheetVideoButtons(video));
      video.addEventListener('pause', () => this.syncSheetVideoButtons(video));
    }

    pauseSheetVideo() {
      const video = this.sheetVideo();
      if (!video) return;
      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {
        /* ignore */
      }
      video.muted = true;
      this.syncSheetVideoButtons(video);
    }

    toggleSheetPlayback() {
      const video = this.sheetVideo();
      if (!video) return;
      if (video.paused) {
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') {
          attempt.catch(() => this.syncSheetVideoButtons(video));
        }
      } else {
        video.pause();
      }
      this.syncSheetVideoButtons(video);
    }

    toggleSheetAudio() {
      const video = this.sheetVideo();
      if (!video) return;
      video.muted = !video.muted;
      if (!video.muted && video.paused) {
        video.play().catch(() => {});
      }
      this.syncSheetVideoButtons(video);
    }

    syncSheetVideoButtons(video) {
      if (!this.sheet || !video) return;
      const playBtn = this.sheet.querySelector('.lookbook-notch-sheet__play');
      const audioBtn = this.sheet.querySelector('.lookbook-notch-sheet__audio');
      playBtn.textContent = video.paused ? 'Play' : 'Pause';
      playBtn.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
      audioBtn.textContent = video.muted ? 'Unmute' : 'Mute';
      audioBtn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      audioBtn.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
    }

    playHotspotVideo(hotspot, withSound, alreadyOpen) {
      const video = hotspot.querySelector('.notch-look__hotspot-card__video');
      const card = hotspot.querySelector('.notch-look__hotspot-card');
      if (!video) return;

      video.playsInline = true;
      this.applyVideoOrientation(video, card);

      const startMuted = () => {
        video.muted = true;
        this.syncMuteControl(hotspot.querySelector('.notch-look__hotspot-card__mute'), true);
        const mutedPlay = video.play();
        if (mutedPlay && typeof mutedPlay.catch === 'function') {
          mutedPlay.catch(() => {});
        }
      };

      if (withSound) {
        video.muted = false;
        this.syncMuteControl(hotspot.querySelector('.notch-look__hotspot-card__mute'), false);
        const attempt = video.play();
        if (attempt && typeof attempt.then === 'function') {
          attempt.catch(() => {
            startMuted();
          });
        }
        return;
      }

      if (!alreadyOpen || video.paused) {
        startMuted();
      }
    }

    toggleMute(hotspot) {
      const video = hotspot.querySelector('.notch-look__hotspot-card__video');
      if (!video) return;

      this.clearCloseTimer();
      this.openHotspot(hotspot, { withSound: false });

      if (video.muted || video.volume === 0) {
        video.muted = false;
        this.syncMuteControl(hotspot.querySelector('.notch-look__hotspot-card__mute'), false);
        if (video.paused) {
          const attempt = video.play();
          if (attempt && typeof attempt.catch === 'function') {
            attempt.catch(() => {
              video.muted = true;
              this.syncMuteControl(hotspot.querySelector('.notch-look__hotspot-card__mute'), true);
              video.play().catch(() => {});
            });
          }
        }
      } else {
        video.muted = true;
        this.syncMuteControl(hotspot.querySelector('.notch-look__hotspot-card__mute'), true);
      }
    }

    syncMuteControl(muteBtn, muted) {
      if (!muteBtn) return;

      const icon = muteBtn.querySelector('.notch-look__hotspot-card__mute-icon');
      const state = muteBtn.querySelector('.notch-look__hotspot-card__mute-state');
      const unmuteLabel = muteBtn.getAttribute('data-label-unmute') || 'Unmute video';
      const muteLabel = muteBtn.getAttribute('data-label-mute') || 'Mute video';

      muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
      muteBtn.setAttribute('aria-label', muted ? unmuteLabel : muteLabel);

      if (icon) {
        icon.classList.toggle('notch-look__hotspot-card__mute-icon--muted', muted);
        icon.classList.toggle('notch-look__hotspot-card__mute-icon--unmuted', !muted);
      }
      if (state) {
        state.textContent = muted ? 'Muted' : 'Unmuted';
      }
    }

    resetHotspotVideo(hotspot) {
      const video = hotspot.querySelector('.notch-look__hotspot-card__video');
      if (!video) return;

      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {
        /* ignore */
      }
      video.muted = true;
      this.syncMuteControl(hotspot.querySelector('.notch-look__hotspot-card__mute'), true);
    }
  }

  customElements.define('lookbook-notch-component', LookbookNotchComponent);
})();
